'use client';

import { useState, useEffect } from 'react';
import Image from 'next/image';
import { Dialog, DialogContent, DialogClose } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import {
  Carousel,
  CarouselContent,
  CarouselItem,
} from '@/components/ui/carousel';
import { ChevronLeft, ChevronRight, X } from 'lucide-react';
import type { CarouselApi } from '@/components/ui/carousel';

interface FoodPhoto {
  id: number;
  src: string;
  alt: string;
}

interface GalleryCarouselProps {
  images: FoodPhoto[];
}

export function GalleryCarousel({ images }: GalleryCarouselProps) {
  const [selectedImageIndex, setSelectedImageIndex] = useState<number | null>(null);
  const [mainApi, setMainApi] = useState<CarouselApi>();
  const [lightboxApi, setLightboxApi] = useState<CarouselApi>();
  const [current, setCurrent] = useState(0);
  const [lightboxCurrent, setLightboxCurrent] = useState(0);

  // Main carousel navigation
  useEffect(() => {
    if (!mainApi) return;

    mainApi.on('select', () => {
      setCurrent(mainApi.selectedScrollSnap());
    });
  }, [mainApi]);

  // Lightbox carousel navigation
  useEffect(() => {
    if (!lightboxApi) return;

    lightboxApi.on('select', () => {
      setLightboxCurrent(lightboxApi.selectedScrollSnap());
    });
  }, [lightboxApi]);

  // Sync lightbox carousel with selected image
  useEffect(() => {
    if (selectedImageIndex !== null && lightboxApi) {
      lightboxApi.scrollTo(selectedImageIndex);
    }
  }, [selectedImageIndex, lightboxApi]);

  const handleImageClick = (index: number) => {
    setSelectedImageIndex(index);
  };

  const handleLightboxNext = () => {
    if (lightboxApi) {
      lightboxApi.scrollNext();
      setSelectedImageIndex(lightboxApi.selectedScrollSnap());
    }
  };

  const handleLightboxPrev = () => {
    if (lightboxApi) {
      lightboxApi.scrollPrev();
      setSelectedImageIndex(lightboxApi.selectedScrollSnap());
    }
  };

  const handleCloseLightbox = () => {
    setSelectedImageIndex(null);
  };

  const handleMainPrev = () => {
    if (mainApi) {
      mainApi.scrollPrev();
    }
  };

  const handleMainNext = () => {
    if (mainApi) {
      mainApi.scrollNext();
    }
  };

  return (
    <>
      {/* Main Carousel */}
      <div className="w-full">
        <Carousel
          opts={{
            align: 'start',
            loop: true,
          }}
          setApi={setMainApi}
          className="w-full"
        >
          <CarouselContent className="-ml-2 md:-ml-4">
            {images.map((image, index) => (
              <CarouselItem
                key={image.id}
                className="pl-2 md:pl-4 basis-full md:basis-1/3 lg:basis-1/4"
              >
                <button
                  onClick={() => handleImageClick(index)}
                  className="group relative w-full aspect-square rounded-2xl overflow-hidden bg-gray-100 border-2 border-[#E8D8C3] hover:border-[#D71920]/40 hover:shadow-xl transition-all duration-300 cursor-pointer focus:outline-none focus:ring-2 focus:ring-[#D71920] focus:ring-offset-2"
                >
                  <Image
                    src={image.src}
                    alt={image.alt}
                    fill
                    className="object-cover group-hover:scale-105 transition-transform duration-300"
                    sizes="(max-width: 640px) 100vw, (max-width: 1024px) 50vw, 25vw"
                  />
                  <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-all duration-300 flex items-center justify-center">
                    <div className="opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                      <div className="h-12 w-12 rounded-full bg-white/90 flex items-center justify-center">
                        <div className="flex gap-1">
                          <div className="w-1 h-1 bg-[#D71920] rounded-full"></div>
                          <div className="w-1 h-1 bg-[#D71920] rounded-full"></div>
                        </div>
                      </div>
                    </div>
                  </div>
                </button>
              </CarouselItem>
            ))}
          </CarouselContent>

          {/* Navigation Arrows */}
          <button
            onClick={handleMainPrev}
            className="absolute left-0 top-1/2 -translate-y-1/2 -translate-x-16 md:-translate-x-20 hidden md:flex h-10 w-10 md:h-12 md:w-12 bg-[#D71920] hover:bg-[#B01017] text-white rounded-full items-center justify-center transition-colors"
            aria-label="Previous slide"
          >
            <ChevronLeft className="h-5 w-5 md:h-6 md:w-6" />
          </button>
          <button
            onClick={handleMainNext}
            className="absolute right-0 top-1/2 -translate-y-1/2 translate-x-16 md:translate-x-20 hidden md:flex h-10 w-10 md:h-12 md:w-12 bg-[#D71920] hover:bg-[#B01017] text-white rounded-full items-center justify-center transition-colors"
            aria-label="Next slide"
          >
            <ChevronRight className="h-5 w-5 md:h-6 md:w-6" />
          </button>

          {/* Mobile Arrow Buttons */}
          <div className="md:hidden flex gap-3 justify-center mt-4">
            <button
              onClick={handleMainPrev}
              className="h-10 w-10 bg-[#D71920] hover:bg-[#B01017] text-white rounded-full flex items-center justify-center transition-colors"
              aria-label="Previous slide"
            >
              <ChevronLeft className="h-5 w-5" />
            </button>
            <button
              onClick={handleMainNext}
              className="h-10 w-10 bg-[#D71920] hover:bg-[#B01017] text-white rounded-full flex items-center justify-center transition-colors"
              aria-label="Next slide"
            >
              <ChevronRight className="h-5 w-5" />
            </button>
          </div>
        </Carousel>

        {/* Dot Indicators */}
        <div className="flex justify-center gap-2 mt-6 md:mt-8">
          {images.map((_, index) => (
            <button
              key={index}
              onClick={() => mainApi?.scrollTo(index)}
              className={`h-2 rounded-full transition-all duration-300 ${
                index === current
                  ? 'bg-[#D71920] w-8'
                  : 'bg-[#1E1E1E]/30 hover:bg-[#1E1E1E]/50 w-2'
              }`}
              aria-label={`Go to slide ${index + 1}`}
            />
          ))}
        </div>
      </div>

      {/* Lightbox Modal */}
      <Dialog open={selectedImageIndex !== null} onOpenChange={handleCloseLightbox}>
        <DialogContent className="max-w-4xl max-h-screen bg-black border-0 p-0 md:p-6 rounded-lg md:rounded-2xl overflow-hidden flex flex-col">
          {/* Close Button */}
          <DialogClose className="absolute top-4 right-4 z-50 h-8 w-8 bg-[#D71920] hover:bg-[#B01017] text-white rounded-full flex items-center justify-center transition-colors">
            <X className="h-5 w-5" />
          </DialogClose>

          {/* Lightbox Carousel */}
          {selectedImageIndex !== null && (
            <div className="flex-1 flex flex-col items-center justify-center gap-4">
              <Carousel
                opts={{
                  align: 'center',
                  loop: true,
                }}
                setApi={setLightboxApi}
                className="w-full"
              >
                <CarouselContent>
                  {images.map((image) => (
                    <CarouselItem key={image.id} className="flex items-center justify-center">
                      <div className="relative w-full h-screen max-h-screen md:max-h-[80vh] flex items-center justify-center">
                        <Image
                          src={image.src}
                          alt={image.alt}
                          fill
                          className="object-contain p-4 md:p-8"
                          sizes="100vw"
                          priority
                        />
                      </div>
                    </CarouselItem>
                  ))}
                </CarouselContent>
              </Carousel>

              {/* Lightbox Navigation */}
              <div className="flex gap-2 md:gap-4 items-center justify-center pb-4 md:pb-0">
                <button
                  onClick={handleLightboxPrev}
                  className="h-10 w-10 md:h-12 md:w-12 bg-[#D71920] hover:bg-[#B01017] text-white rounded-full flex items-center justify-center transition-colors"
                  aria-label="Previous image"
                >
                  <ChevronLeft className="h-5 w-5 md:h-6 md:w-6" />
                </button>

                <span className="text-white font-bold text-sm md:text-base px-4">
                  {lightboxCurrent + 1} / {images.length}
                </span>

                <button
                  onClick={handleLightboxNext}
                  className="h-10 w-10 md:h-12 md:w-12 bg-[#D71920] hover:bg-[#B01017] text-white rounded-full flex items-center justify-center transition-colors"
                  aria-label="Next image"
                >
                  <ChevronRight className="h-5 w-5 md:h-6 md:w-6" />
                </button>
              </div>

              {/* Lightbox Dot Indicators */}
              <div className="flex justify-center gap-1 md:gap-2 pb-4 md:pb-0 flex-wrap max-w-md">
                {images.map((_, index) => (
                  <button
                    key={index}
                    onClick={() => lightboxApi?.scrollTo(index)}
                    className={`h-2 rounded-full transition-all duration-300 ${
                      index === lightboxCurrent
                        ? 'bg-[#D71920] w-6'
                        : 'bg-white/40 hover:bg-white/60 w-2'
                    }`}
                    aria-label={`Go to image ${index + 1}`}
                  />
                ))}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
